// ADHD Home mode — pure logic (ADR-0015, design handoff §"Reguły wyliczania
// bMode" and §"Wariant A · Cisza" copy). No React here on purpose: the rules
// are unit-tested in adhd.test.ts without rendering anything.

export type AdhdVariant = "A" | "B" | "C";

// Variant B traffic-light state. Note: "stop" uses a graphite palette, never
// alarm red (design handoff §A11y / §Tone of voice).
export type BMode = "ok" | "soft" | "stop";

/**
 * Derive variant B's traffic-light state from how much daily kcal is left and
 * the local hour (0-23). Handoff rules:
 *   - `ok`:   kcal_remaining > 800  AND  hour < 21
 *   - `stop`: kcal_remaining <= 200 OR over the limit (negative remaining)
 *   - `soft`: everything in between (incl. hour >= 21)
 *
 * `stop` is checked first so "over the limit late at night" still reads stop.
 */
export function computeBMode(kcalRemaining: number, hourLocal: number): BMode {
  if (kcalRemaining <= 200) return "stop";
  if (kcalRemaining > 800 && hourLocal < 21) return "ok";
  return "soft";
}

// Stable i18n keys for variant A's single contextual support sentence. The
// literal copy lives in lib/i18n/adhd.ts (adhdCopy.aSub*); this module only
// decides which one applies.
export type VariantACopyKey = "bigWindow" | "evening" | "littleLeft" | "limitDone";

/**
 * Pick variant A's contextual sentence (design handoff §"Wariant A · Cisza"):
 *   - over the limit            → "limitDone"  ("Limit zjedzony. Spokój.")
 *   - < 300 kcal left           → "littleLeft" ("Mało zostało. Może herbata?")
 *   - > 500 kcal & after lunch  → "evening"    ("Wieczór jest twój...")
 *   - otherwise (lots of room)  → "bigWindow"  ("Spokojnie. Masz spore okno...")
 *
 * "afterLunch" is true once the user has passed their usual lunch hour; the
 * caller derives it from the local hour (handoff treats lunch as the
 * before/after pivot, not a precise clock time).
 */
export function pickVariantACopyKey(kcalRemaining: number, afterLunch: boolean): VariantACopyKey {
  if (kcalRemaining <= 0) return "limitDone";
  if (kcalRemaining < 300) return "littleLeft";
  if (kcalRemaining > 500 && afterLunch) return "evening";
  return "bigWindow";
}

// The local hour from which variant A treats the day as "after lunch" and
// variant C nudges toward dinner. Kept here so the rule is one constant.
export const LUNCH_HOUR = 14;
